/**
 * Date module.
 * @module src/date
 */
import { isInteger, isBetween, isBlank } from '..';
import { toMilliseconds } from './time';
const timezoneFormat = /GMT-\d{4}/;
const dateFormats = {
  'pt-BR': /^(?<day>\d{2})[./-](?<month>\d{2})[./-](?<year>\d{4})$/,
  en: /^(?<year>\d{4})[./-](?<month>\d{2})[./-](?<day>\d{2})$/
};
const monthInfos = {
  '01': {
    normal: {
      days: 31
    },
    leap: {
      days: 31
    }
  },
  '02': {
    normal: {
      days: 28
    },
    leap: {
      days: 29
    }
  },
  '03': {
    normal: {
      days: 31
    },
    leap: {
      days: 31
    }
  },
  '04': {
    normal: {
      days: 30
    },
    leap: {
      days: 30
    }
  },
  '05': {
    normal: {
      days: 31
    },
    leap: {
      days: 31
    }
  },
  '06': {
    normal: {
      days: 30
    },
    leap: {
      days: 30
    }
  },
  '07': {
    normal: {
      days: 31
    },
    leap: {
      days: 31
    }
  },
  '08': {
    normal: {
      days: 31
    },
    leap: {
      days: 31
    }
  },
  '09': {
    normal: {
      days: 30
    },
    leap: {
      days: 30
    }
  },
  10: {
    normal: {
      days: 31
    },
    leap: {
      days: 31
    }
  },
  11: {
    normal: {
      days: 30
    },
    leap: {
      days: 30
    }
  },
  12: {
    normal: {
      days: 31
    },
    leap: {
      days: 31
    }
  }
};
/**
 * Returns the given argument is a date or not.
 *
 * @function isDate
 *
 * @param {Any} date - the value to check if is a date or not.
 * @return {Boolean} - if the given argument is date or not.
 *
 * @example
 * isDate('2010-10-10');
 * // returns false
 *
 * @example
 * isDate(null);
 * // returns false
 *
 * @example
 * isDate(undefined);
 * // returns false
 *
 * @example
 * isDate(new Date('Bad String'));
 * // returns false
 *
 * @example
 * isDate(new Date('2010-01-01'));
 * // returns true
 *
 */

export const isDate = date => date instanceof Date && !isNaN(date);
/**
 * Returns the given argument represents a leap year or not.
 *
 * @function isLeapYear
 *
 * @param {String|Number} year - the value to check if is a leap year or not.
 * @return {Boolean} - if the given argument is leap year or not.
 *
 * @example
 * isLeapYear('903B');
 * // returns false
 *
 * @example
 * isLeapYear('1004')
 * // returns true
 *
 * @example
 * isLeapYear(1004)
 * // returns true
 *
 **/

export const isLeapYear = year => isInteger(year) && (year % 400 === 0 || year % 4 === 0 && year % 100 !== 0);
/**
 * Returns if a date string is valid considering locale format month day including February.
 *
 * @function isValidDate
 *
 * @param {String} date - String containing the date to be tested.
 * @return {Boolean} - if the date is valid or not.
 *
 * @example
 * isValidDate('2010-14-10', { locale: 'en' });
 * // returns false
 *
 * @example
 * isValidDate('2010-11-31', { locale: 'en' });
 * // returns false
 *
 * @example
 * isValidDate('2010-11-00', { locale: 'en' });
 * // returns false
 *
 * @example
 * isValidDate('2021-02-29', { locale: 'en' });
 * // returns false
 *
 * @example
 * isValidDate('2010-10-10', { locale: 'en' });
 * // returns true
 *
 * @example
 * isValidDate('2020-02-29', { locale: 'en' });
 * // returns true
 *
 * @example
 * isValidDate('10/14/2010');
 * // returns false
 *
 * @example
 * isValidDate('31/11/2010');
 * // returns false
 *
 * @example
 * isValidDate('00/11/2010');
 * // returns false
 *
 * @example
 * isValidDate('29/02/2021');
 * // returns false
 *
 * @example
 * isValidDate('10/10/2010');
 * // returns true
 *
 * @example
 * isValidDate('29/02/2020');
 * // returns true
 *
 */

export const isValidDate = (date, _ref) => {
  let _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? 'pt-BR' : _ref$locale;
  const dateFormat = dateFormats[locale];
  if (isBlank(dateFormat) || !dateFormat.test(date)) return false;
  const _dateFormat$exec$grou = dateFormat.exec(date).groups,
        day = _dateFormat$exec$grou.day,
        month = _dateFormat$exec$grou.month,
        year = _dateFormat$exec$grou.year;
  const monthInfo = monthInfos[month];
  if (isBlank(monthInfo)) return false;
  const monthDays = isLeapYear(year) ? monthInfo.leap.days : monthInfo.normal.days;
  return isBetween(parseInt(day), 1, monthDays);
};
/**
 * Returns the date timezone.
 *
 * @function dateTimezone
 *
 * @param {Date} date - the date to extract the timezone.
 * @return {String} - the date timezone
 *
 * @example
 * dateTimezone('2010-10-10');
 * // throws error
 *
 * @example
 * dateTimezone(new Date('2010-01-01'));
 * // returns 'GMT-0300'
 *
 */

export const dateTimezone = date => {
  if (!isDate(date)) throw new Error(`The argument '${date}' must be a Date object.`);
  return date.toString().match(timezoneFormat)[0];
};

const dateInTimezone = (date, timeZone) => {
  if (!isDate(new Date(date))) throw new Error(`The argument '${date}' must be a Date object.`);
  const parsedDate = new Date(`${date} ${timeZone}`);
  return parsedDate;
};
/**
 * Returns the date in the current timezone.
 *
 * @function dateInCurrentTimezone
 *
 * @param {String} date - the date to be parsed in current timezone.
 * @return {Date} - the date parsed in current timezone.
 *
 * @example
 * dateInCurrentTimezone('Bad String');
 * // throws error
 *
 * @example
 * dateInCurrentTimezone('2017-01-01');
 * // returns 2017-01-01T02:00:00.000Z
 *
 */


export const dateInCurrentTimezone = date => {
  const currentTimezone = dateTimezone(new Date());
  return dateInTimezone(date, currentTimezone);
};
/**
 * Returns the date calculating interval to advance.
 *
 * @function advanceDate
 *
 * @param {Date} date - date to calculate interval.
 * @param {Number} interval - date to calculate interval.
 * @param {('milliseconds'|'seconds'|'minutes'|'hours'|'days')} timeUnit - string to indicate which unit the interval means.
 * @return {Date} - the date with interval calculated.
 *
 * @example
 * advanceDate('Bad String', 3, 'days');
 * // throws error
 *
 * @example
 * advanceDate(new Date('2017-01-01T02:00:00.000Z'), 4, 'days');
 * // returns 2017-01-05T02:00:00.000Z
 *
 */

export const advanceDate = (date, interval, timeUnit) => {
  if (!isDate(new Date(date))) throw new Error(`The argument '${date}' must be a Date object.`);
  return new Date(date.getTime() + toMilliseconds(interval, timeUnit));
};